import React from 'react';
import useThemeCanvas, { hexToRgb } from '../useThemeCanvas';

// Waves — layered flowing sine ribbons. Each layer has its own frequency,
// amplitude, phase, and palette color. Translucent fills overlap into a soft
// aurora-like flow, with a glowing crest line on top of each ribbon.

const LAYERS = [
  { amp: 70, freq: 0.004,  speed: 0.18, baseY: 0.40, phase: 0,   paletteIdx: 0, fillAlpha: 0.16 },
  { amp: 55, freq: 0.006,  speed: 0.22, baseY: 0.50, phase: 0.7, paletteIdx: 1, fillAlpha: 0.18 },
  { amp: 85, freq: 0.003,  speed: 0.14, baseY: 0.62, phase: 1.4, paletteIdx: 2, fillAlpha: 0.20 },
  { amp: 45, freq: 0.008,  speed: 0.28, baseY: 0.72, phase: 2.1, paletteIdx: 3, fillAlpha: 0.24 },
  { amp: 95, freq: 0.0025, speed: 0.10, baseY: 0.82, phase: 2.8, paletteIdx: 4, fillAlpha: 0.28 },
];

export default function WavesTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: () => {},
    draw: (ctx, w, h, state, t) => {
      // Moody gradient backdrop
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      if (dark) {
        grad.addColorStop(0, '#030712');
        grad.addColorStop(0.5, '#0c1d3e');
        grad.addColorStop(1, '#1e1b4b');
      } else {
        grad.addColorStop(0, '#eaf1fe');
        grad.addColorStop(0.5, '#dbe6fb');
        grad.addColorStop(1, '#d7defb');
      }
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // 'lighter' makes overlapping translucent ribbons glow on dark; on light
      // backgrounds that washes out, so layer them normally instead.
      ctx.globalCompositeOperation = dark ? 'lighter' : 'source-over';

      const step = 8;
      for (const L of LAYERS) {
        const idx = (L.paletteIdx + (colorMode === 'cycling' ? Math.floor(t * 0.3) : 0)) % palette.length;
        const rgb = hexToRgb(palette[idx]);
        const baseY = h * L.baseY;
        const phaseT = t * L.speed + L.phase;

        // Compute the crest once and reuse for both fill and stroke.
        const pts = [];
        for (let x = -10; x <= w + 10; x += step) {
          const y = baseY
            + Math.sin(x * L.freq + phaseT) * L.amp
            + Math.sin(x * L.freq * 2.3 + phaseT * 1.4) * L.amp * 0.4
            + Math.sin(x * L.freq * 0.5 + phaseT * 0.6) * L.amp * 0.3;
          pts.push([x, y]);
        }

        // Filled body
        ctx.beginPath();
        ctx.moveTo(-10, h + 20);
        for (const [x, y] of pts) ctx.lineTo(x, y);
        ctx.lineTo(w + 10, h + 20);
        ctx.closePath();
        const fillG = ctx.createLinearGradient(0, baseY - L.amp, 0, h);
        fillG.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${L.fillAlpha * (dark ? 1.4 : 1.1)})`);
        fillG.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
        ctx.fillStyle = fillG;
        ctx.fill();

        // Glowing crest line
        ctx.beginPath();
        pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
        ctx.strokeStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${dark ? 0.85 : 0.55})`;
        ctx.lineWidth = 1.6;
        ctx.shadowBlur = dark ? 16 : 6;
        ctx.shadowColor = `rgba(${rgb.r},${rgb.g},${rgb.b},${dark ? 0.9 : 0.4})`;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      ctx.globalCompositeOperation = 'source-over';
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
